import { useMemo } from 'react';

import { ActivityIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';
import type { ContactActivity } from '@/shared/types/contacts';
import { type DateBucket, DATE_BUCKET_ORDER, getDateBucket } from '@/util/dateUtils';

import { ActivityRow } from './ActivityRow';
import { useContact } from '../hooks/useContact';
import { useContactActivities } from '../hooks/useContactActivities';
import { useLiveFollowUpProjections } from '../hooks/useLiveFollowUpProjections';

interface ActivityTabProps {
  contactId: string;
}

const BUCKET_LABEL_KEY: Record<DateBucket, string> = {
  today: 'contacts.activity_today',
  yesterday: 'contacts.activity_yesterday',
  last_7_days: 'contacts.activity_last_7_days',
  last_30_days: 'contacts.activity_last_30_days',
  older: 'contacts.activity_older',
};

const groupByBucket = (activities: ContactActivity[]): Record<DateBucket, ContactActivity[]> => {
  const now = Date.now();
  const buckets: Record<DateBucket, ContactActivity[]> = {
    today: [],
    yesterday: [],
    last_7_days: [],
    last_30_days: [],
    older: [],
  };
  for (const activity of activities) {
    buckets[getDateBucket(activity.timestamp, now)].push(activity);
  }
  return buckets;
};

export const ActivityTab = ({ contactId }: ActivityTabProps) => {
  const { activities, status } = useContactActivities(contactId);
  const { contact } = useContact(contactId);
  const liveProjectionIds = useLiveFollowUpProjections(contactId);
  const { t } = useTranslation();

  const buckets = useMemo(() => groupByBucket(activities), [activities]);

  if (status === 'pending') {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-bg-muted-50">
          <ActivityIcon size={24} className="text-foreground-muted" />
        </div>
        <h3 className="text-sm font-semibold text-foreground">{t('contacts.no_activities')}</h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-muted">
          {t('contacts.no_activities_description')}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6 flex w-full flex-col px-2">
      {DATE_BUCKET_ORDER.map((bucket) => {
        const rows = buckets[bucket];
        if (rows.length === 0) {
          return null;
        }
        return (
          <section key={bucket} className="flex flex-col gap-3">
            <div className="flex items-center gap-3 py-3">
              <div className="h-px flex-1 bg-stroke" />
              <span className="text-xs text-foreground-muted">{t(BUCKET_LABEL_KEY[bucket])}</span>
              <div className="h-px flex-1 bg-stroke" />
            </div>
            <div className="flex flex-col gap-3">
              {rows.map((activity) => (
                <ActivityRow
                  key={activity.activity_id}
                  activity={activity}
                  contactName={contact?.name}
                  showProjection={liveProjectionIds.has(activity.activity_id)}
                />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
};
